import { BookOpen, Users, Award, Lightbulb } from 'lucide-react';

export const OfrecimientosPage = () => {
  const programas = [
    {
      id: 'elemental',
      icon: <BookOpen className="w-12 h-12 text-[#43b6b8]" />,
      title: 'Programa Educativo Elemental',
      description: 'Apoyo académico integral para estudiantes de escuela elemental, fortaleciendo bases sólidas en lectoescritura, matemáticas, ciencias y desarrollo socioemocional.',
      image: 'https://customer-assets.emergentagent.com/job_formando-lideres/artifacts/2ootpy36_d1505ed5-d984-4a60-a3cb-6ba80b60fc45.JPG',
      alt: 'Grupo de estudiantes en proyecto colaborativo'
    },
    {
      id: 'intermedia',
      icon: <Lightbulb className="w-12 h-12 text-[#43b6b8]" />,
      title: 'Programa Educativo Intermedia',
      description: 'Refuerzo académico enfocado en desarrollar pensamiento crítico, autonomía en el aprendizaje y habilidades avanzadas para el nivel intermedio.',
      image: 'https://customer-assets.emergentagent.com/job_formando-lideres/artifacts/7vm405h7_1a22a3af-b9ab-475a-9193-e27a6405e384.JPG',
      alt: 'Estudiantes pintando un mural creativo'
    },
    {
      id: 'superior',
      icon: <Award className="w-12 h-12 text-[#43b6b8]" />,
      title: 'Programa Educativo Superior',
      description: 'Preparación académica completa para estudiantes de escuela superior, con énfasis en organización, técnicas de estudio y preparación para el éxito universitario.',
      image: 'https://customer-assets.emergentagent.com/job_formando-lideres/artifacts/hk3dhf3o_87724a32-3c72-47d4-8672-c8a0ac1c08c6.JPG',
      alt: 'Estudiantes en proyecto cultural de Reyes Magos'
    }
  ];

  return (
    <div className="pt-20" data-testid="ofrecimientos-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#43b6b8] to-[#359a9c]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
            Ofrecimientos
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Programas educativos y servicios diseñados para apoyar el desarrollo académico de cada estudiante.
          </p>
        </div>
      </section>

      {/* Programa K-12 */}
      <section className="py-20 md:py-28" id="programa-k12" data-testid="programa-k12-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d3b163] mb-4 block">Programa Educativo</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 mb-4">
              Apoyo Académico K-12
            </h2>
            <p className="text-base text-stone-700 max-w-3xl mx-auto">
              Ofrecemos programas educativos adaptados a cada nivel escolar, desde elemental hasta superior, con enfoque en aprendizaje personalizado y desarrollo integral.
            </p>
          </div>

          <div className="space-y-20">
            {programas.map((programa, index) => (
              <div
                key={programa.id}
                className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${index % 2 === 0 ? '' : 'md:flex-row-reverse'}`}
                data-testid={`programa-${programa.id}`}
              >
                <div className={index % 2 === 0 ? 'md:order-1' : 'md:order-2'}>
                  <div className="mb-6">{programa.icon}</div>
                  <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight text-stone-900 mb-4">
                    {programa.title}
                  </h3>
                  <p className="text-base text-stone-700 leading-relaxed">
                    {programa.description}
                  </p>
                </div>
                <div className={`rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                  <img src={programa.image} alt={programa.title} className="w-full h-full object-cover" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultoría y Asesoría */}
      <section className="py-20 md:py-28 bg-[#F3F0EA]" id="consultoria" data-testid="consultoria-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <img
                src="https://customer-assets.emergentagent.com/job_formando-lideres/artifacts/vxqx4xl7_PHOTO-2026-05-28-18-21-46.jpg"
                alt="Dra. Melissa Núñez Robles, fundadora de Rise & Shine Learning Zone, sosteniendo un teléfono con el logo de la institución"
                className="w-full h-full object-cover aspect-[4/3]"
                style={{
                  objectPosition: 'center 22%',
                  filter: 'brightness(1.04) contrast(1.05) saturate(1.02)'
                }}
                loading="lazy"
              />
            </div>
            <div>
              <Users className="w-12 h-12 text-[#43b6b8] mb-6" />
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 mb-6">
                Consultoría y Asesoría Educativa
              </h2>
              <p className="text-base text-stone-700 leading-relaxed mb-4">
                En Rise & Shine Learning Zone, nuestra fundadora ofrece servicios de consultoría y asesoría educativa dirigidos no solo a padres, sino también a educadores, colegios y organizaciones.
              </p>
              <p className="text-base text-stone-700 leading-relaxed mb-4">
                A través de talleres, seminarios y acompañamiento personalizado, brindamos herramientas prácticas para fortalecer el desarrollo académico, emocional y social de los estudiantes.
              </p>
              <p className="text-base text-stone-700 leading-relaxed">
                También se trabajan áreas como inteligencia emocional, estrategias educativas y apoyo en procesos de aprendizaje.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-900 mb-6">
            ¿Listo para comenzar?
          </h2>
          <p className="text-lg text-stone-700 mb-8">
            Descubre cómo nuestros programas pueden ayudar a tu hijo a alcanzar su máximo potencial.
          </p>
          <a
            href="/informacion-admision"
            data-testid="cta-solicita-info-ofrecimientos"
            className="inline-block bg-[#d3b163] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#b89548] transition-all active:scale-95 shadow-md"
          >
            Solicita Información
          </a>
        </div>
      </section>
    </div>
  );
};
